import React from 'react';
import { ArrowDown, Building2, MapPin } from 'lucide-react';
import { Button } from './ui/button';
import { projectInfo } from '../data/mock';

const HeroSection = () => {
  const scrollToOverview = () => {
    const element = document.querySelector('#overview');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const scrollToContact = () => {
    const element = document.querySelector('#contact');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
      {/* Background Image with Overlay */}
      <div className="absolute inset-0">
        <img
          src={projectInfo.heroImage}
          alt="Stadium Plaza Center Aerial View"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-slate-900/80 via-slate-900/60 to-slate-900/90" />
      </div>

      {/* Decorative Elements */}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
        <div className="absolute top-1/4 -left-20 w-96 h-96 bg-emerald-500/10 rounded-full blur-3xl" />
        <div className="absolute bottom-1/4 -right-20 w-96 h-96 bg-teal-500/10 rounded-full blur-3xl" />
      </div>

      {/* Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-6 py-32 text-center">
        {/* Badge */}
        <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm border border-white/20 rounded-full px-4 py-2 mb-8">
          <span className="w-2 h-2 bg-emerald-400 rounded-full animate-pulse" />
          <span className="text-white/90 text-sm font-medium">Now Pre-Leasing</span>
        </div>

        {/* Main Heading */}
        <h1 className="text-4xl md:text-6xl lg:text-7xl font-bold text-white mb-6 leading-tight">
          <span className="block">{projectInfo.name}</span>
          <span className="block text-emerald-400 mt-2 text-3xl md:text-4xl lg:text-5xl font-medium">
            {projectInfo.tagline}
          </span>
        </h1>

        {/* Location Badge */}
        <div className="flex items-center justify-center gap-2 text-white/80 mb-8">
          <MapPin size={20} className="text-emerald-400" />
          <span className="text-lg">
            {projectInfo.address}, {projectInfo.city}, {projectInfo.state} {projectInfo.zip}
          </span>
        </div>

        {/* Description */}
        <p className="text-white/70 text-lg md:text-xl max-w-3xl mx-auto mb-12 leading-relaxed">
          {projectInfo.description}
        </p>

        {/* CTA Buttons */}
        <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
          <Button
            onClick={scrollToContact}
            size="lg"
            className="bg-emerald-600 hover:bg-emerald-700 text-white px-8 py-6 text-lg rounded-lg shadow-lg shadow-emerald-600/25 transition-all hover:shadow-xl hover:shadow-emerald-600/30"
          >
            <Building2 className="mr-2" size={20} />
            Request Leasing Info
          </Button>
          <Button
            onClick={scrollToOverview}
            variant="outline"
            size="lg"
            className="border-white/30 text-white hover:bg-white/10 px-8 py-6 text-lg rounded-lg backdrop-blur-sm"
          >
            Explore Project
          </Button>
        </div>

        {/* Stats Row */}
        <div className="mt-20 grid grid-cols-2 md:grid-cols-4 gap-8 max-w-4xl mx-auto">
          {[
            { value: '±1.5', label: 'Acres', suffix: '' },
            { value: '13,603', label: 'Total SF', suffix: '' },
            { value: '50+', label: 'Parking Spaces', suffix: '' },
            { value: 'Q4', label: '2027 Delivery', suffix: '' }
          ].map((stat, index) => (
            <div key={index} className="text-center">
              <div className="text-3xl md:text-4xl font-bold text-white mb-1">
                {stat.value}{stat.suffix}
              </div>
              <div className="text-white/60 text-sm">{stat.label}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Scroll Indicator */}
      <button
        onClick={scrollToOverview}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 text-white/60 hover:text-white transition-colors animate-bounce"
      >
        <ArrowDown size={28} />
      </button>
    </section>
  );
};

export default HeroSection;
